import { create } from 'zustand';
import axios from '../utils/axios';
import { ENDPOINTS } from '../config/api';

const recalculateLocalCart = (cart) => {
  if (!cart || !cart.items) {
    return { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 };
  }
  const totalPrice = cart.items.reduce((total, item) => {
    const price = item.product?.salePrice || item.product?.price || 0;
    return total + (price * item.quantity);
  }, 0);
  return {
    ...cart,
    totalPrice,
    totalDiscount: 0,
    finalPrice: totalPrice
  };
};

const saveCartSnapshot = (cart) => {
  try {
    if (cart) {
      localStorage.setItem('mashow_cart_snapshot', JSON.stringify(cart));
    }
  } catch (e) {}
};

const getInitialCart = () => {
  try {
    const snapshot = localStorage.getItem('mashow_cart_snapshot');
    if (snapshot) return JSON.parse(snapshot);
    const guestCart = localStorage.getItem('guest_cart');
    return guestCart ? JSON.parse(guestCart) : { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 };
  } catch (e) {
    return { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 };
  }
};

const useCartStore = create((set, get) => ({
  cart: getInitialCart(),
  loading: false,
  error: null,

  // Get cart
  getCart: async () => {
    set({ loading: true, error: null });
    try {
      const useAuthStore = (await import('./useAuthStore')).default;
      const { isAuthenticated } = useAuthStore.getState();

      if (isAuthenticated) {
        const response = await axios.get(ENDPOINTS.CART.BASE);
        const cartData = response.data.data;
        saveCartSnapshot(cartData);
        set({ cart: cartData, loading: false });
        return response;
      } else {
        const guestCartJson = localStorage.getItem('guest_cart');
        const guestCart = guestCartJson
          ? JSON.parse(guestCartJson)
          : { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 };
        saveCartSnapshot(guestCart);
        set({ cart: guestCart, loading: false });
        return { data: { data: guestCart } };
      }
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Add to cart
  addToCart: async (item) => {
    set({ loading: true, error: null });
    try {
      const useAuthStore = (await import('./useAuthStore')).default;
      const { isAuthenticated } = useAuthStore.getState();

      if (isAuthenticated) {
        const response = await axios.post(ENDPOINTS.CART.BASE, item);
        const cartData = response.data.data;
        saveCartSnapshot(cartData);
        set({ cart: cartData, loading: false });
        return response;
      } else {
        const { productId, quantity, variant } = item;
        const response = await axios.get(ENDPOINTS.PRODUCTS.BY_ID(productId));
        const product = response.data.data;

        let localCart = get().cart;
        if (!localCart || !localCart.items) {
          localCart = { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 };
        }

        const existingItemIndex = localCart.items.findIndex(
          i => i.product?._id === productId && JSON.stringify(i.variant) === JSON.stringify(variant)
        );

        if (existingItemIndex > -1) {
          localCart.items[existingItemIndex].quantity += quantity;
        } else {
          localCart.items.push({
            _id: 'guest_' + Math.random().toString(36).substring(2, 9),
            product: product,
            quantity: quantity,
            variant: variant,
            price: product.price,
            salePrice: product.salePrice,
            ringSize: variant?.ringSize || ''
          });
        }

        const calculatedCart = recalculateLocalCart(localCart);
        localStorage.setItem('guest_cart', JSON.stringify(calculatedCart));
        saveCartSnapshot(calculatedCart);
        set({ cart: calculatedCart, loading: false });
        return { data: { data: calculatedCart } };
      }
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Update cart item
  updateCartItem: async (itemId, quantity) => {
    set({ loading: true, error: null });
    try {
      const useAuthStore = (await import('./useAuthStore')).default;
      const { isAuthenticated } = useAuthStore.getState();

      if (isAuthenticated) {
        const response = await axios.put(ENDPOINTS.CART.ITEM(itemId), { quantity });
        const cartData = response.data.data;
        saveCartSnapshot(cartData);
        set({ cart: cartData, loading: false });
        return response;
      } else {
        let localCart = get().cart;
        if (localCart && localCart.items) {
          const itemIndex = localCart.items.findIndex(i => i._id === itemId);
          if (itemIndex > -1) {
            localCart.items[itemIndex].quantity = quantity;
            const calculatedCart = recalculateLocalCart(localCart);
            localStorage.setItem('guest_cart', JSON.stringify(calculatedCart));
            saveCartSnapshot(calculatedCart);
            set({ cart: calculatedCart, loading: false });
          } else {
            set({ loading: false });
          }
        } else {
          set({ loading: false });
        }
        return { data: { data: get().cart } };
      }
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Remove from cart
  removeFromCart: async (itemId) => {
    set({ loading: true, error: null });
    try {
      const useAuthStore = (await import('./useAuthStore')).default;
      const { isAuthenticated } = useAuthStore.getState();

      if (isAuthenticated) {
        const response = await axios.delete(ENDPOINTS.CART.ITEM(itemId));
        const cartData = response.data.data;
        saveCartSnapshot(cartData);
        set({ cart: cartData, loading: false });
        return response;
      } else {
        let localCart = get().cart;
        if (localCart && localCart.items) {
          localCart.items = localCart.items.filter(i => i._id !== itemId);
          const calculatedCart = recalculateLocalCart(localCart);
          localStorage.setItem('guest_cart', JSON.stringify(calculatedCart));
          saveCartSnapshot(calculatedCart);
          set({ cart: calculatedCart, loading: false });
        } else {
          set({ loading: false });
        }
        return { data: { data: get().cart } };
      }
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Clear cart
  clearCart: async () => {
    set({ loading: true, error: null });
    try {
      const useAuthStore = (await import('./useAuthStore')).default;
      const { isAuthenticated } = useAuthStore.getState();

      if (isAuthenticated) {
        const response = await axios.delete(ENDPOINTS.CART.BASE);
        const cartData = response.data.data;
        saveCartSnapshot(cartData);
        set({ cart: cartData, loading: false });
        return response;
      } else {
        const emptyCart = { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 };
        localStorage.setItem('guest_cart', JSON.stringify(emptyCart));
        saveCartSnapshot(emptyCart);
        set({ cart: emptyCart, loading: false });
        return { data: { data: emptyCart } };
      }
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Sync cart to server
  syncCart: async () => {
    const guestCartJson = localStorage.getItem('guest_cart');
    if (!guestCartJson) return;

    try {
      const guestCart = JSON.parse(guestCartJson);
      if (guestCart && guestCart.items && guestCart.items.length > 0) {
        for (const item of guestCart.items) {
          await axios.post(ENDPOINTS.CART.BASE, {
            productId: item.product?._id,
            quantity: item.quantity,
            variant: item.variant
          });
        }
      }
      localStorage.removeItem('guest_cart');
      await get().getCart();
    } catch (error) {
      console.error('Failed to sync guest cart:', error);
    }
  },

  // Clear cart store locally (used on logout)
  clearCartStore: () => {
    set({ cart: { items: [], totalPrice: 0, totalDiscount: 0, finalPrice: 0 } });
  },

  // Apply coupon
  applyCoupon: async (code) => {
    set({ loading: true, error: null });
    try {
      const useAuthStore = (await import('./useAuthStore')).default;
      const { isAuthenticated } = useAuthStore.getState();

      if (!isAuthenticated) {
        throw new Error('Kupon kullanmak için giriş yapmalısınız.');
      }

      const response = await axios.post(ENDPOINTS.CART.COUPON, { code });
      set({ cart: response.data.data, loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Remove coupon
  removeCoupon: async () => {
    set({ loading: true, error: null });
    try {
      const response = await axios.delete(ENDPOINTS.CART.COUPON);
      set({ cart: response.data.data, loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Add item to cart (helper method)
  addItem: async (productId, quantity = 1, variant = null) => {
    return get().addToCart({ productId, quantity, variant });
  },

  // Alias methods for Cart.jsx compatibility
  fetchCart: async () => {
    return get().getCart();
  },

  updateItem: async (itemId, quantity) => {
    return get().updateCartItem(itemId, quantity);
  },

  removeItem: async (itemId) => {
    return get().removeFromCart(itemId);
  },

  // Computed properties
  get items() {
    return get().cart?.items || [];
  },

  // Get cart item count
  getCartItemCount: () => {
    const { cart } = get();
    return cart?.items?.reduce((total, item) => total + item.quantity, 0) || 0;
  },

  // Get total price
  getTotalPrice: () => {
    const { cart } = get();
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      const price = item.product?.salePrice || item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
  },
}));

export default useCartStore;

